import { useEffect, useState } from "react";
import { VetrinaPreview } from "@/components/VetrinaPreview";
import { api, apiError } from "@/lib/apiClient";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Handshake, Plus, Copy, Link2 } from "lucide-react";

const EMPTY = { codice: "", ente_promotore: "", cooperativa_nome: "", mondo_conf: false, in_vetrina: false, logo_url: "",
  categorie_ammesse: [], prodotti_abilitati: ["infortuni"],
  fascia_max: "", riferimento_autorizzazione: "", validita_da: "", validita_a: "", referente: "", note: "", attivo: true };

export default function ReteSolidaleAdmin() {
  const [items, setItems] = useState([]);
  const [meta, setMeta] = useState({ categorie: [], prodotti: [] });
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/admin/convenzioni").then(({ data }) => setItems(data));
  useEffect(() => { load(); api.get("/admin/convenzioni/meta").then(({ data }) => setMeta(data)); }, []);

  const toggleArr = (key, v) => setForm((f) => ({ ...f, [key]: f[key].includes(v) ? f[key].filter((x) => x !== v) : [...f[key], v] }));
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const create = async () => {
    setSaving(true);
    try {
      await api.post("/admin/convenzioni", form);
      toast.success(`Convenzione ${form.codice} creata`);
      setOpen(false); setForm(EMPTY); load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  const toggleAttivo = async (c) => {
    try { await api.put(`/admin/convenzioni/${c.id}`, { attivo: !c.attivo }); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const toggleVetrina = async (c) => {
    try { await api.put(`/admin/convenzioni/${c.id}`, { in_vetrina: !c.in_vetrina }); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const copyLink = (c) => {
    const url = `${window.location.origin}/rete-solidale?c=${c.codice}`;
    navigator.clipboard?.writeText(url);
    toast.success("Link pubblico copiato");
  };

  return (
    <div className="space-y-6" data-testid="rete-solidale-admin">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 font-heading text-3xl font-semibold text-forest"><Handshake className="h-7 w-7 text-terracotta" /> Rete Solidale — Convenzioni</h1>
          <p className="mt-1 text-muted-foreground">Convenzioni con enti, cooperative, fondazioni. Ogni codice ha un link pubblico unico. Nessuno sconto è mostrato all'esterno.</p>
        </div>
        <div className="flex items-center gap-2">
          <a href="/cooperative-convenzionate" target="_blank" rel="noopener noreferrer" data-testid="open-vetrina-link"
            className="inline-flex items-center gap-1.5 rounded-xl border border-forest/30 px-3 py-2 text-sm font-medium text-forest hover:bg-sage/40">
            <Link2 className="h-4 w-4" /> Apri vetrina
          </a>
          <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button data-testid="conv-new-button" className="gap-2 rounded-xl bg-terracotta hover:bg-terracotta-hover"><Plus className="h-4 w-4" /> Nuova convenzione</Button></DialogTrigger>
          <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
            <DialogHeader><DialogTitle className="font-heading text-forest">Nuova convenzione</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5"><Label>Codice *</Label><Input data-testid="conv-codice" placeholder="RSA-CSVSAR-26" value={form.codice} onChange={(e) => setForm({ ...form, codice: e.target.value.toUpperCase() })} /></div>
                <div className="space-y-1.5"><Label>Ente promotore *</Label><Input data-testid="conv-ente" value={form.ente_promotore} onChange={set("ente_promotore")} /></div>
              </div>
              <div className="rounded-lg border border-border/60 bg-sage/20 p-3 space-y-2.5">
                <div className="space-y-1.5"><Label>Nome cooperativa (in vetrina)</Label>
                  <Input data-testid="conv-cooperativa-nome" placeholder="Es. Cooperativa Sociale La Rondine" value={form.cooperativa_nome} onChange={set("cooperativa_nome")} />
                  <p className="text-[11px] text-muted-foreground">Mostrato nella vetrina pubblica "Cooperative Convenzionate". Se vuoto si usa l'ente promotore. Logo: per ora lasciato vuoto.</p>
                </div>
                <div className="flex flex-wrap gap-4">
                  <label className="flex cursor-pointer items-center gap-2 text-sm text-forest"><Checkbox data-testid="conv-mondo-conf" checked={form.mondo_conf} onCheckedChange={(v) => setForm({ ...form, mondo_conf: !!v })} /> Mondo Confcooperative</label>
                  <label className="flex cursor-pointer items-center gap-2 text-sm text-forest"><Checkbox data-testid="conv-in-vetrina" checked={form.in_vetrina} onCheckedChange={(v) => setForm({ ...form, in_vetrina: !!v })} /> Pubblica in vetrina</label>
                </div>
              </div>
              <div>
                <Label>Categorie ammesse</Label>
                <div className="mt-1.5 grid grid-cols-2 gap-1.5">
                  {meta.categorie.map((c) => (
                    <label key={c.value} className="flex cursor-pointer items-center gap-2 text-sm text-forest"><Checkbox checked={form.categorie_ammesse.includes(c.value)} onCheckedChange={() => toggleArr("categorie_ammesse", c.value)} /> {c.label}</label>
                  ))}
                </div>
              </div>
              <div>
                <Label>Prodotti abilitati</Label>
                <div className="mt-1.5 flex flex-wrap gap-3">
                  {meta.prodotti.map((p) => (
                    <label key={p.value} className="flex cursor-pointer items-center gap-2 text-sm text-forest"><Checkbox checked={form.prodotti_abilitati.includes(p.value)} onCheckedChange={() => toggleArr("prodotti_abilitati", p.value)} /> {p.label}{!p.attivo && <span className="text-xs text-muted-foreground">(in arrivo)</span>}</label>
                  ))}
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div className="space-y-1.5"><Label>Fascia max (interno)</Label><Input data-testid="conv-fascia" placeholder="A / B" value={form.fascia_max} onChange={set("fascia_max")} /></div>
                <div className="space-y-1.5"><Label>Valida dal</Label><Input type="date" value={form.validita_da} onChange={set("validita_da")} /></div>
                <div className="space-y-1.5"><Label>Valida al</Label><Input type="date" value={form.validita_a} onChange={set("validita_a")} /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5"><Label>Riferimento autorizzazione</Label><Input value={form.riferimento_autorizzazione} onChange={set("riferimento_autorizzazione")} placeholder="Prot./email Assimoco" /></div>
                <div className="space-y-1.5"><Label>Referente</Label><Input value={form.referente} onChange={set("referente")} /></div>
              </div>
              <div className="space-y-1.5"><Label>Note</Label><Textarea value={form.note} onChange={set("note")} /></div>
              <p className="rounded-lg bg-amber-50 p-2 text-[11px] text-amber-800">La fascia di sconto è solo interna: mai mostrata al cliente, mai detratta dalle provvigioni. Le percentuali si definiscono a parte.</p>
            </div>
            <DialogFooter><Button onClick={create} disabled={saving || !form.codice || !form.ente_promotore} data-testid="conv-save" className="bg-terracotta hover:bg-terracotta-hover">Crea convenzione</Button></DialogFooter>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      <VetrinaPreview items={items} onToggle={toggleVetrina} />

      <Card className="rounded-xl border-border/60">
        <Table>
          <TableHeader><TableRow className="bg-sage/50">
            <TableHead>Codice</TableHead><TableHead>Ente / Cooperativa</TableHead><TableHead>Prodotti</TableHead><TableHead>Validità</TableHead><TableHead>Fascia</TableHead><TableHead>Stato</TableHead><TableHead>Vetrina</TableHead><TableHead></TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {items.length === 0 && <TableRow><TableCell colSpan={8} className="py-8 text-center text-sm text-muted-foreground">Nessuna convenzione. Creane una per generare il link pubblico.</TableCell></TableRow>}
            {items.map((c) => (
              <TableRow key={c.id} data-testid={`conv-row-${c.codice}`}>
                <TableCell className="font-medium text-forest">{c.codice}</TableCell>
                <TableCell>{c.cooperativa_nome ? <span>{c.ente_promotore || "—"}<br/><span className="text-xs text-forest">{c.cooperativa_nome}{c.mondo_conf ? " · Conf" : ""}</span></span> : c.ente_promotore}</TableCell>
                <TableCell className="text-xs">{(c.prodotti_abilitati || []).join(", ") || "—"}</TableCell>
                <TableCell className="text-xs">{c.validita_da || "—"} → {c.validita_a || "—"}</TableCell>
                <TableCell className="text-xs">{c.fascia_max || "—"}</TableCell>
                <TableCell>
                  <button onClick={() => toggleAttivo(c)} data-testid={`conv-toggle-${c.codice}`}
                    className={`rounded-full border px-2.5 py-0.5 text-xs font-medium ${c.attivo ? "border-emerald-200 bg-emerald-50 text-emerald-700" : "border-border/60 bg-muted text-muted-foreground"}`}>
                    {c.attivo ? "Attiva" : "Sospesa"}
                  </button>
                </TableCell>
                <TableCell>
                  <button onClick={() => toggleVetrina(c)} data-testid={`conv-vetrina-${c.codice}`}
                    className={`rounded-full border px-2.5 py-0.5 text-xs font-medium ${c.in_vetrina ? "border-forest/30 bg-forest/10 text-forest" : "border-border/60 bg-muted text-muted-foreground"}`}>
                    {c.in_vetrina ? "In vetrina" : "Nascosta"}
                  </button>
                </TableCell>
                <TableCell className="text-right">
                  <Button size="sm" variant="ghost" onClick={() => copyLink(c)} data-testid={`conv-link-${c.codice}`} className="gap-1.5 text-forest"><Link2 className="h-3.5 w-3.5" /> Link</Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}
